import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/landing/Header";

const Footer = lazy(() => import("@/components/landing/Footer"));

const Security = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Security & Data Handling – PieFolio AI</title>
        <meta
          name="description"
          content="How PieFolio AI handles your data: bank-grade encryption, third-party AI providers, account deletion, and no selling of personal data."
        />
        <link rel="canonical" href="https://getpiefolio.com/security" />
        <link rel="alternate" type="text/markdown" href="https://getpiefolio.com/security.md" />
        <meta property="og:title" content="PieFolio AI – Security & Data Handling" />
        <meta property="og:description" content="How PieFolio AI protects your portfolio data: TLS 1.2+ in transit, AES-256 at rest, no brokerage access, AI provider boundaries, and one-tap account deletion." />
        <meta property="og:url" content="https://getpiefolio.com/security" />
      </Helmet>
      <Header />
      <main className="pt-24 pb-24">
        <article className="container mx-auto px-6 lg:px-12 max-w-3xl prose-styled">
          <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-6">
            Security & data handling
          </h1>
          <p className="text-lg text-muted-foreground mb-8">
            PieFolio AI is a portfolio tracker, not a broker. We never move money and your assets
            stay with your existing broker or platform. The data you enter is yours, and you can
            delete your account from inside the app at any time.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-3">Encryption</h2>
          <p className="text-muted-foreground mb-6">
            All traffic between the app and our backend uses TLS 1.2+. Data at rest is encrypted on
            managed cloud infrastructure with industry-standard AES-256.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-3">No brokerage connection</h2>
          <p className="text-muted-foreground mb-6">
            We do not connect to your brokerage account. You add holdings manually or via CSV. We
            cannot place trades, transfer funds or read balances from third-party institutions.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-3">AI providers</h2>
          <p className="text-muted-foreground mb-6">
            AI analysis is generated by third-party AI providers. We send only the structured
            portfolio context needed to produce the report (positions, sectors, weights) and never
            personal identifiers. AI responses are cached for ~12 hours to control cost and latency.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-3">Account deletion</h2>
          <p className="text-muted-foreground mb-6">
            Open the app → Settings → Account → Delete account. Deletion is permanent and removes
            all portfolios, holdings and AI reports linked to your account.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-3">We do not sell data</h2>
          <p className="text-muted-foreground mb-6">
            We never sell or rent personal data. See our{" "}
            <a href="/privacy" className="text-primary underline">privacy policy</a> for the full
            details that cover both the website and the mobile app.
          </p>

          <h2 className="text-2xl font-bold mt-10 mb-3">Contact</h2>
          <p className="text-muted-foreground">
            Security questions or vulnerability reports:{" "}
            <a href="mailto:security@getpiefolio.com" className="text-primary underline">
              security@getpiefolio.com
            </a>
          </p>
        </article>
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </main>
    </div>
  );
};

export default Security;
